import type {
  CreateUserInput,
  GrantMembershipInput,
  InviteUserInput,
  UpdateUserInput,
} from "./validation";
import type { GovernedBy } from "@praesto/shared-types";

async function bearer(
  url: string,
  idToken: string,
  init: {
    method?: string;
    headers?: Record<string, string>;
    body?: unknown;
  } = {},
): Promise<Response> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${idToken}`,
    ...(init.headers ?? {}),
  };
  const reqInit: RequestInit = {};
  if (init.method !== undefined) reqInit.method = init.method;
  if (init.body !== undefined) {
    headers["Content-Type"] = "application/json";
    reqInit.body = JSON.stringify(init.body);
  }
  reqInit.headers = headers;
  return fetch(url, reqInit);
}

function qs(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") usp.append(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export interface UserSummary {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly status: "pending" | "active" | "disabled" | "archived";
  /**
   * Membership facts added by `GET /api/users` (Fase 2 — Liste, see
   * `docs/superpowers/specs/2026-07-21-layout-full-width-design.md`).
   * Optional so older cached responses / callers stay structurally
   * compatible: this payload crosses the wire.
   */
  readonly role?: "tenant_owner" | "manager_hotel" | "manager_functional" | "staff";
  /** Union of department codes across all of the person's memberships. */
  readonly departments?: ReadonlyArray<string>;
  /** Hotels the person belongs to (via membership). */
  readonly hotelIds?: ReadonlyArray<string>;
}

/**
 * `GET /api/users/:uid` returns the raw user doc, which carries fields beyond
 * `UserSummary` — notably `governedBy` (provenance marker written by inbound
 * HR connectors; see `GovernedBy` in `@praesto/shared-types`). This type is
 * additive/superset so it stays structurally compatible with `UserSummary`
 * consumers.
 */
export interface UserDetail extends UserSummary {
  readonly governedBy?: GovernedBy;
}

export interface MembershipSummary {
  readonly id: string;
  readonly userId: string;
  readonly hotelId: string;
  readonly role: "tenant_owner" | "manager_hotel" | "manager_functional" | "staff";
  readonly departments: ReadonlyArray<string>;
}

export async function inviteUser(
  apiBase: string,
  input: InviteUserInput,
  idToken: string,
): Promise<{ uid: string; magicLinkSentTo: string; warning?: string }> {
  const res = await bearer(`${apiBase}/api/users/invite`, idToken, {
    method: "POST",
    body: input,
  });
  if (!res.ok) throw new Error(`inviteUser failed: ${res.status}`);
  return (await res.json()) as {
    uid: string;
    magicLinkSentTo: string;
    warning?: string;
  };
}

export async function createUser(
  apiBase: string,
  input: CreateUserInput,
  idToken: string,
): Promise<{ uid: string }> {
  const res = await bearer(`${apiBase}/api/users/create`, idToken, {
    method: "POST",
    body: input,
  });
  if (!res.ok) throw new Error(`createUser failed: ${res.status}`);
  return (await res.json()) as { uid: string };
}

/**
 * One page of the roster. `nextCursor` is present only while more people
 * remain — the caller must follow it rather than assume the first page is the
 * whole staff, which is exactly the mistake a bare server-side `.limit()`
 * would have made invisible.
 */
export async function listUsers(
  apiBase: string,
  query: { hotelId?: string; role?: string; status?: string; limit?: number; after?: string },
  idToken: string,
): Promise<{ users: ReadonlyArray<UserSummary>; nextCursor?: string }> {
  const res = await bearer(`${apiBase}/api/users${qs(query)}`, idToken);
  if (!res.ok) throw new Error(`listUsers failed: ${res.status}`);
  return (await res.json()) as { users: ReadonlyArray<UserSummary>; nextCursor?: string };
}

export async function getUser(
  apiBase: string,
  uid: string,
  idToken: string,
): Promise<{
  user: UserDetail;
  memberships: ReadonlyArray<MembershipSummary>;
}> {
  const res = await bearer(`${apiBase}/api/users/${uid}`, idToken);
  if (!res.ok) throw new Error(`getUser failed: ${res.status}`);
  return (await res.json()) as {
    user: UserDetail;
    memberships: ReadonlyArray<MembershipSummary>;
  };
}

export async function updateUser(
  apiBase: string,
  uid: string,
  input: UpdateUserInput,
  idToken: string,
): Promise<void> {
  const res = await bearer(`${apiBase}/api/users/${uid}`, idToken, {
    method: "PUT",
    body: input,
  });
  if (!res.ok) throw new Error(`updateUser failed: ${res.status}`);
}

export async function disableUser(apiBase: string, uid: string, idToken: string): Promise<void> {
  const res = await bearer(`${apiBase}/api/users/${uid}/disable`, idToken, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`disableUser failed: ${res.status}`);
}

export async function enableUser(apiBase: string, uid: string, idToken: string): Promise<void> {
  const res = await bearer(`${apiBase}/api/users/${uid}/enable`, idToken, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`enableUser failed: ${res.status}`);
}

export async function grantMembership(
  apiBase: string,
  uid: string,
  input: GrantMembershipInput,
  idToken: string,
): Promise<{ membershipId: string }> {
  const res = await bearer(`${apiBase}/api/users/${uid}/memberships`, idToken, {
    method: "POST",
    body: input,
  });
  if (!res.ok) throw new Error(`grantMembership failed: ${res.status}`);
  return (await res.json()) as { membershipId: string };
}

/**
 * GET /api/users/sample-data — owner-only read-only check, used by
 * `SampleDataBanner` (apps/web/src/onboarding) to decide whether to show its
 * "remove sample data" nudge without calling the destructive purge endpoint.
 */
export async function checkSampleData(
  apiBase: string,
  idToken: string,
): Promise<{ hasSampleData: boolean; count: number }> {
  const res = await bearer(`${apiBase}/api/users/sample-data`, idToken);
  if (!res.ok) throw new Error(`checkSampleData failed: ${res.status}`);
  return (await res.json()) as { hasSampleData: boolean; count: number };
}

/**
 * POST /api/users/sample-data/purge — owner-only. Deletes every remaining
 * `sample: true` doc (departments not confirmed in the onboarding wizard +
 * the sample SOPs, which the wizard never touches).
 */
export async function purgeSampleData(
  apiBase: string,
  idToken: string,
): Promise<{ deleted: number }> {
  const res = await bearer(`${apiBase}/api/users/sample-data/purge`, idToken, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`purgeSampleData failed: ${res.status}`);
  return (await res.json()) as { deleted: number };
}

export async function revokeMembership(
  apiBase: string,
  uid: string,
  hotelId: string,
  idToken: string,
): Promise<void> {
  const res = await bearer(`${apiBase}/api/users/${uid}/memberships/${hotelId}`, idToken, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`revokeMembership failed: ${res.status}`);
}
